// leadStatuses.ts — single source of truth for lead statuses on the frontend.
// Keep in sync with models/lead.go (ValidLeadStatuses) on the backend.
//
//   🆕 new → 📞 contacted → 💡 interested → 🔁 follow_up → ✅ qualified → 🏆 converted
//   exits (any stage): 🌱 nurture · 🚫 not_interested · ⚠️ invalid

export interface StatusStyle {
  bg: string;
  color: string;
  border: string;
}

export interface LeadStatusDef {
  value: string;
  label: string;
  style: StatusStyle;
}

export const LEAD_STATUSES: LeadStatusDef[] = [
  { value: 'new', label: 'New', style: { bg: 'rgba(17,85,80,0.08)', color: '#115550', border: 'rgba(17,85,80,0.22)' } },
  { value: 'contacted', label: 'Contacted', style: { bg: 'rgba(59,130,246,0.1)', color: '#1d4ed8', border: 'rgba(59,130,246,0.3)' } },
  { value: 'interested', label: 'Interested', style: { bg: 'rgba(124,58,237,0.1)', color: '#6d28d9', border: 'rgba(124,58,237,0.3)' } },
  { value: 'follow_up', label: 'Follow up', style: { bg: 'rgba(245,158,11,0.12)', color: '#b45309', border: 'rgba(245,158,11,0.35)' } },
  { value: 'qualified', label: 'Qualified', style: { bg: 'rgba(200,233,75,0.22)', color: '#4a5e00', border: 'rgba(200,233,75,0.5)' } },
  { value: 'converted', label: 'Converted', style: { bg: 'rgba(22,163,74,0.12)', color: '#15803d', border: 'rgba(22,163,74,0.3)' } },
  { value: 'nurture', label: 'Nurture', style: { bg: 'rgba(6,182,212,0.1)', color: '#0e7490', border: 'rgba(6,182,212,0.3)' } },
  { value: 'not_interested', label: 'Not interested', style: { bg: 'rgba(239,68,68,0.1)', color: '#c62828', border: 'rgba(239,68,68,0.3)' } },
  { value: 'invalid', label: 'Invalid', style: { bg: 'rgba(107,114,128,0.12)', color: '#4b5563', border: 'rgba(107,114,128,0.3)' } },
];

export const LEAD_STATUS_VALUES: string[] = LEAD_STATUSES.map((s) => s.value);

// Old values that may still exist on leads saved before the new flow.
// Used only so they display / edit sensibly until the data is migrated.
const LEGACY_STATUS_MAP: Record<string, string> = {
  lost: 'not_interested',
  closed: 'converted',
  'in-progress': 'contacted',
  in_progress: 'contacted',
};

const FALLBACK_STYLE: StatusStyle = { bg: 'rgba(156,163,175,0.12)', color: '#4b5563', border: 'rgba(156,163,175,0.3)' };

const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase();

/** Returns a valid status key for any input (maps legacy values, defaults to 'new'). */
export const toValidStatus = (s: string | null | undefined): string => {
  const n = norm(s);
  if (LEAD_STATUS_VALUES.includes(n)) return n;
  return LEGACY_STATUS_MAP[n] ?? 'new';
};

export const getStatusStyle = (s: string | null | undefined): StatusStyle => {
  const n = norm(s);
  const key = LEAD_STATUS_VALUES.includes(n) ? n : LEGACY_STATUS_MAP[n];
  return LEAD_STATUSES.find((d) => d.value === key)?.style ?? FALLBACK_STYLE;
};

export const statusLabel = (s: string | null | undefined): string => {
  const n = norm(s);
  if (!n) return '-';
  const key = LEAD_STATUS_VALUES.includes(n) ? n : LEGACY_STATUS_MAP[n];
  const def = LEAD_STATUSES.find((d) => d.value === key);
  if (def) return def.label;
  const t = n.replace(/[_-]/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// ── Flow helpers (used by the lead details page) ─────────────────────────────

/** Main path, left to right. */
export const MAIN_FLOW: string[] = ['new', 'contacted', 'interested', 'follow_up', 'qualified', 'converted'];

/** Exits — available from any stage. */
export const EXIT_STATUSES: string[] = ['nurture', 'not_interested', 'invalid'];

/**
 * What a lead can move to next (shown as the blinking nodes + quick buttons).
 * contacted forks into interested OR follow_up; both then converge on qualified.
 */
export const NEXT_STEPS: Record<string, string[]> = {
  new: ['contacted'],
  contacted: ['interested', 'follow_up'],
  interested: ['qualified'],
  follow_up: ['qualified'],
  qualified: ['converted'],
};

export const STATUS_EMOJI: Record<string, string> = {
  new: '🆕',
  contacted: '📞',
  interested: '💡',
  follow_up: '🔁',
  qualified: '✅',
  converted: '🏆',
  nurture: '🌱',
  not_interested: '🚫',
  invalid: '⚠️',
};

export const STATUS_HINT: Record<string, string> = {
  new: 'Back to a fresh lead nobody has touched.',
  contacted: 'Someone has reached out to this lead.',
  interested: 'They showed real interest.',
  follow_up: 'A callback or reminder is due.',
  qualified: 'Good fit and ready to convert.',
  converted: 'Became a customer. This counts as a conversion in reports.',
  nurture: 'Not now — keep in touch for later.',
  not_interested: 'They said no.',
  invalid: 'Wrong number, fake, duplicate or junk.',
};